import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@material-ui/core";
import _ from "lodash";

import withAuthority from "../../../../components/Auth/withAuthority";
import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { useRouteMatch } from "react-router";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { fetchAllFilterLocations } from "../../../../services/locationApp/locationFilterService";
import { getCookie } from "../../../../utils/cookies";
import VoidSummeryInfoNode from "./VoidSummeryInfoNode";
import { voidSummeryFilterObject } from "../../../../utils/consts/list";
import { CustomTheme } from "../../../../types/customTheme";
import SaleReport from "../../../../components/common/SaleReport";
import { getFilterListFromArrayObject } from "../../../../utils/commonArrayMap";
import { fetchAllVoidSummeryInfo } from "../../../../services/salesApp/voidSummeryServices";
import { fetchAllVGetLogsInfo } from "../../../../services/salesApp/logsService";
import FilterOption from "./FilterOption";
import { fetchAllUserInfo } from "../../../../services/salesApp/salesService";

/**
 * LogsPage Component:
 *
 * This component is responsible for displaying the VAT summary report for void transactions.
 * It retrieves void summary information from the backend API based on the selected filters
 * such as location, date range, etc., and presents the data in a tabular format.
 */
const LogsPage: React.FunctionComponent<{}> = () => {
  const [error, setError] = useState("");
  const [orderId, setOrderId] = useState("");
  const [logsNode, setLogsNode] = useState([]);
  const [genericNode, setGenericsNode] = useState([]);
  const [kdsLogs, setKdsLogs] = useState([]);
  const [userDetails, setUserDetails] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [locationSelectorList, setLocationSelectorList] = useState([]);
  const [isGeneric, setIsGeneric] = useState(true);
  const [isOrderLogs, setIsOrderLogs] = useState(true); // Changed: default to true to show date selection
  const [isKdsLogs, setIsKdsLogs] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");

  const match: any = useRouteMatch();
  const idToken = getCookie("idToken");

  const handleGetLogsInfoGeneric = async () => {
    setIsLoading(true);
    try {
      const res: any = await fetchAllVGetLogsInfo(
        match.params.locationId,
        "generic",
      );
      setGenericsNode(res.data.data);
      setLogsNode(res.data.data);
      setIsGeneric(true);
      setIsOrderLogs(false);
      setIsOrderDetails(false);
      setIsLoading(false);
    } catch (error) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoading(false);
    }
  };

  const handleGetLogsInfoKds = async () => {
    setIsLoading(true);
    try {
      const res: any = await fetchAllVGetLogsInfo(
        match.params.locationId,
        "kds",
      );
      setKdsLogs(res.data.data);
      setIsLoading(false);
    } catch (error) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoading(false);
    }
  };

  const getUserInfo = async () => {
    try {
      const res = await fetchAllUserInfo(idToken, match.params.locationId);
      setUserDetails(res.data.data);
      getAllFilterLocation([match.params.locationId]);
    } catch (err) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoading(false);
    }
  };

  const handleGetLogsInfos = async () => {
    setIsLoading(true);
    try {
      const id = orderId.replace(".", "_");
      const res: any = await fetchAllVGetLogsInfo(match.params.locationId, id);
      setLogsNode(res.data.data);
      setIsLoading(false);
      setIsGeneric(false);
      setIsOrderLogs(false);
      setIsOrderDetails(false);
    } catch (error) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoading(false);
    }
  };

  const [isOrderDetails, setIsOrderDetails] = useState(false);
  
  const handleOrderKeyClick = async (key: string) => {
    setOrderId(key);
    setIsLoading(true);
    try {
      const id = key.replace(".", "_");
      const res: any = await fetchAllVGetLogsInfo(match.params.locationId, id);
      setLogsNode(res.data.data);
      setIsLoading(false);
      setIsGeneric(false);
      setIsOrderLogs(false);
      setIsOrderDetails(true);
    } catch (error) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoading(false);
    }
  };

  const getAllFilterLocation = async (searchName: any) => {
    fetchAllFilterLocations("")
      .then((res) => {
        let locationList: any = [];
        /* Setting up the list of locations as needed to select locations. */
        if (!_.isEmpty(res.data.data)) {
          res.data.data.map((location: any) =>
            locationList.push({
              id: location.id,
              label: location.businessDisplayName,
            }),
          );
        }
        // REMOVED: handleGetLogsInfoGeneric(); - Don't auto-load generic logs
        handleGetLogsInfoKds(); // Keep this if needed
        setLocationSelectorList(locationList);
        setIsLoading(false);
      })
      .catch(() => {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    document.title = "Sale - Logs";
    // setIsLoading(true);
    getUserInfo();
  }, []);

  const handleSubmitGeneric = () => {
    setIsGeneric(true);
    setIsLoading(true);
    // If genericNode is empty, fetch it
    if (genericNode.length === 0) {
      handleGetLogsInfoGeneric();
    } else {
      setLogsNode(genericNode);
       setIsLoading(false);
    }
    setIsOrderLogs(false);
    setIsOrderDetails(false);
    setOrderId("");
  };

  const handleSubmitKds = () => {
    setIsGeneric(true);
    // If kdsLogs is empty, fetch it
    if (kdsLogs.length === 0) {
      handleGetLogsInfoKds();
    } else {
      setLogsNode(kdsLogs);
    }
    setIsOrderLogs(false);
    setIsOrderDetails(false);
    setOrderId("");
  };

  const handleSubmitOrderLogs = () => {
    setLogsNode([]);
    setIsOrderLogs(true);
    setIsGeneric(false);
    setOrderId("");
  };

  const theme: CustomTheme = useTheme();
  
  return (
    <>
      <Box>
        <Typography
          variant="h4"
          style={{ fontWeight: "bold", cursor: "pointer", marginTop: "16px" }}
          color="secondary"
          align="left"
        >
          Logs
        </Typography>
        <FilterOption
          orderId={orderId}
          setOrderId={setOrderId}
          handleGetLogsInfo={handleGetLogsInfos}
          handleSubmitGeneric={handleSubmitGeneric}
          handleSubmitKds={handleSubmitKds}
          handleSubmitOrderLogs={handleSubmitOrderLogs}
        />
        <VoidSummeryInfoNode
          logsNode={logsNode}
          userDetails={userDetails}
          isLoading={isLoading}
          locationSelectorList={locationSelectorList}
          isGeneric={isGeneric}
          isOrderLogs={isOrderLogs}
          handleOrderKeyClick={handleOrderKeyClick}
          setIsOrderLogs={setIsOrderLogs}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          isOrderDetails={isOrderDetails}
          onShowGenericLogs={handleSubmitGeneric}
        />
        <DefaultAlert
          open={!!error}
          handleClose={() => setError("")}
          message={error}
          severity="error"
        />
      </Box>
    </>
  );
};

export default withAuthority(LogsPage, Authorities.SALE_READ);